import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import dayjs from "dayjs";
import { getLocale } from "react-i18nify";

import {
  Box,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
// import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
// import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";

import * as productAnalyticApi from "src/api/dashboard/productAnalytic";

import font from "src/styles/font";
import device from "src/styles/device";
import { convertIntToCurrency } from "src/common";
import { DarkGrey, GreyStroke, White } from "src/styles/color";

import { UseUpload } from "src/context/UploadProductContext";
import { LoadingContext } from "src/context/LoadingContext";

import DailyViewsChart from "./chart/DailyViewsChart";
import PopularCategoryChart from "./chart/PopularCategoryChart";
import TopViewsTable from "./table/TopViewsTable";
import PopularTrends from "./table/PopularTrends";
import PopularKeywordsList from "./table/PopularKeywords";
import UsageProductFeatures from "./table/UsageProductFeatures";

const BoxWrapper = styled.div`
  width: 100%;
  height: 100%;
  /* height: auto;
  min-height: 100%; */
  padding: 24px;
  border-radius: 16px;
  background-color: #ffffff;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
  justify-content: center;
  align-items: center;
  /* margin-bottom: 32px; */

  @media ${device.tabletL} {
    padding: 24px 16px;
  }
  @media ${device.mobileL} {
    padding: 20px 16px;
  }
`;

export const StyledSelect = styled(Select)`
  border-radius: 16px;
  background-color: ${White};
  text-align: center;

  .MuiSelect-select {
    padding: 8px 16px;
  }
  &.MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${DarkGrey};
  }
  .Mui-disabled,
  &.Mui-disabled .MuiOutlinedInput-notchedOutline {
    border-color: ${GreyStroke};
  }

  @media ${device.tabletL} {
    .MuiSelect-select {
      padding: 8px 12px;
    }
  }
`;

export const StyledMenuItem = styled(MenuItem)`
  &.MuiMenuItem-root {
    line-height: 24px;
    min-height: 40px;
    padding: 8px 24px;
  }

  @media ${device.tabletL} {
    &.MuiMenuItem-root {
      padding: 8px 16px;
    }
  }
`;

const DashboardAdmin = () => {
  const { masterdata } = UseUpload();
  const { setLoading } = useContext(LoadingContext);

  const { seller_data } = masterdata;
  const sellerOptions = seller_data.map((sel) => {
    return {
      ...sel,
      userRoles: [1],
      value: sel.seller,
      label: sel.seller.company_name,
    };
  });

  const today = new Date();
  const last7Days = new Date();
  last7Days.setDate(today.getDate() - 7);
  const last28Days = new Date();
  last28Days.setDate(today.getDate() - 28);
  const last90Days = new Date();
  last90Days.setDate(today.getDate() - 90);
  const lastMonth = new Date();
  lastMonth.setMonth(today.getMonth() - 1);

  const timeRanges = [
    {
      label: {
        th: "7 วันที่ผ่านมา",
        en: "Last 7 days",
      },
      value: 7,
      date: last7Days,
    },
    {
      label: {
        th: "28 วันที่ผ่านมา",
        en: "Last 28 days",
      },
      value: 28,
      date: last28Days,
    },
    {
      label: {
        th: "90 วันที่ผ่านมา",
        en: "Last 90 days",
      },
      value: 90,
      date: last90Days,
    },
  ];

  const [selectedFilters, setSelectedFilters] = useState({
    seller: "all", // default to all sellers - can be updated to specific seller data when selected
    days: 28,
    startDate: "",
    endDate: "",
    compareStart: "",
    compareEnd: "",
  });
  const [selectedTimeRangeData, setSelectedTimeRangeData] = useState({
    label: {
      th: "28 วันที่ผ่านมา",
      en: "Last 28 days",
    },
    value: 28,
    date: last28Days,
  });

  const [summaryData, setSummaryData] = useState({
    totalPageViews: 0,
    sellerViewCount: 0,
    storeAgentAdds: 0,
  });
  const [dailyViews, setDailyViews] = useState([]);
  const [topviewsData, setTopViewsData] = useState([]);
  const [popularCategoriesData, setPopularCategoriesData] = useState([]);
  const [popularTrendsData, setPopularTrendsData] = useState({
    sellerInTrend: null, // boolean or null to indicate if the seller is in trend or not
    trendingTypes: [],
  });
  const [popularKeywordsData, setPopularKeywordsData] = useState([]);
  const [usageProductFeaturesData, setUsageProductFeaturesData] = useState([]);

  const fetchSalesData = async () => {
    try {
      setLoading(true);
      const params = {
        days: selectedFilters.days,
        startDate: !!selectedFilters.startDate
          ? dayjs(selectedFilters.startDate).format("YYYY-MM-DD")
          : "",
        endDate: !!selectedFilters.endDate
          ? dayjs(selectedFilters.endDate).format("YYYY-MM-DD")
          : "",
        sellerId:
          selectedFilters.seller === "all" ? "" : selectedFilters.seller._id,
      };

      const analyticSummary = await productAnalyticApi.getKpiSummary(params);
      setSummaryData(analyticSummary);

      const allData = await productAnalyticApi.getAllSummary(params);
      // console.log("-----allData----", allData);
      const {
        dailyViews,
        topSkus,
        categoryShare,
        searchTerms,
        productTrends,
        // storeAgentAdds,
      } = allData;
      setDailyViews(dailyViews.rows);
      setTopViewsData(topSkus.rows);
      setPopularCategoriesData(categoryShare.rows);
      setPopularKeywordsData(searchTerms.rows);
      setPopularTrendsData(productTrends);

      const featureUsage =
        await productAnalyticApi.getFeatureEventsByAdmin(params);
      setUsageProductFeaturesData(featureUsage);
    } catch (error) {
      console.error("Error fetching sales data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSalesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters]);

  const handleTimerange = (e) => {
    const findedRange = timeRanges.find(
      (range) => range.value === e.target.value,
    );
    setSelectedTimeRangeData(findedRange);

    let selectedValue = e.target.value;
    setSelectedFilters((prev) => ({ ...prev, days: selectedValue }));
  };

  const handleSellerChange = (e) => {
    setSelectedFilters((prev) => ({ ...prev, seller: e.target.value }));
  };

  return (
    <>
      <Grid container spacing={{ xs: 2, md: 3 }}>
        <Grid item xs={6} md={3.5}>
          <BoxWrapper>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: { xs: "flex-end", md: "space-between" },
                gap: { xs: 1, md: 2 },
                flexWrap: { xs: "wrap", md: "nowrap" },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  width: { xs: "100%", md: "max-content" },
                  "&.MuiTypography-root": { fontSize: font.size.xs },
                }}
              >
                ยอดเข้าชมสินค้า
              </Typography>

              <Typography
                variant="h5"
                sx={{ "&.MuiTypography-root": { fontWeight: 600 } }}
              >
                {convertIntToCurrency(summaryData.totalPageViews, false)}
              </Typography>

              <Typography
                variant="body2"
                sx={{ "&.MuiTypography-root": { fontSize: font.size.xs } }}
              >
                ครั้ง
              </Typography>
            </Box>
          </BoxWrapper>
        </Grid>

        <Grid item xs={6} md={3.5}>
          <BoxWrapper>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: { xs: "flex-end", md: "space-between" },
                gap: { xs: 1, lg: 2 },
                flexWrap: { xs: "wrap", md: "nowrap" },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  width: { xs: "100%", md: "max-content" },
                  "&.MuiTypography-root": { fontSize: font.size.xs },
                }}
              >
                ยอดเข้าชมหน้าร้าน
              </Typography>

              <Typography
                variant="h5"
                sx={{ "&.MuiTypography-root": { fontWeight: 600 } }}
              >
                {selectedFilters.seller === "all"
                  ? "-"
                  : convertIntToCurrency(summaryData.sellerViewCount, false)}
              </Typography>

              <Typography
                variant="body2"
                sx={{ "&.MuiTypography-root": { fontSize: font.size.xs } }}
              >
                ครั้ง
              </Typography>
            </Box>
          </BoxWrapper>
        </Grid>

        <Grid
          item
          xs={12}
          md={5}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            flexWrap: { xs: "nowrap", md: "wrap" },
            gap: 1,
          }}
        >
          {/* Filter by Seller */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="flex-end"
            gap={1}
            sx={{ width: { xs: "49%", md: "max-content" } }}
          >
            <Typography
              variant="body2"
              sx={{ "&.MuiTypography-root": { fontSize: font.size.xs } }}
            >
              {getLocale() === "th" ? "ร้านค้า : " : "Seller : "}
            </Typography>
            <FormControl
              sx={{
                width: { xs: "100%", md: "max-content" },
                maxWidth: { xs: "unset", md: 200 },
              }}
            >
              <StyledSelect
                labelId="seller-select-label"
                id="seller-select"
                value={selectedFilters.seller}
                onChange={handleSellerChange}
                MenuProps={{
                  PaperProps: {
                    sx: { borderRadius: 4, ".MuiList-root": { padding: 0 } },
                  },
                }}
              >
                <StyledMenuItem value={"all"}>
                  {getLocale() === "th" ? "ร้านค้าทั้งหมด" : "All Sellers"}
                </StyledMenuItem>
                {sellerOptions.map((seller, index) => (
                  <StyledMenuItem key={index} value={seller.value}>
                    {seller.label}
                  </StyledMenuItem>
                ))}
              </StyledSelect>
            </FormControl>
          </Stack>

          {/* Filter by Time Range */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="flex-end"
            gap={1}
            sx={{ width: { xs: "49%", md: "max-content" } }}
          >
            <Typography
              variant="body2"
              sx={{ "&.MuiTypography-root": { fontSize: font.size.xs } }}
            >
              {getLocale() === "th" ? "ช่วงเวลา : " : "Time Range : "}
            </Typography>
            <FormControl
              sx={{
                width: { xs: "100%", md: "max-content" },
                maxWidth: { xs: "unset", md: 200 },
              }}
            >
              <StyledSelect
                labelId="timerange-select-label"
                id="timerange-select"
                value={selectedFilters.days}
                onChange={handleTimerange}
                MenuProps={{
                  PaperProps: {
                    sx: { borderRadius: 4, ".MuiList-root": { padding: 0 } },
                  },
                }}
              >
                {timeRanges.map((range, index) => (
                  <StyledMenuItem key={index} value={range.value}>
                    {getLocale() === "th" ? range.label.th : range.label.en}
                  </StyledMenuItem>
                ))}
              </StyledSelect>
            </FormControl>
          </Stack>
        </Grid>

        <Grid item xs={12}>
          <BoxWrapper>
            <DailyViewsChart
              data={dailyViews}
              selectedTimeRangeData={selectedTimeRangeData}
            />
          </BoxWrapper>
        </Grid>

        {/* Top views Ranking */}
        <Grid item xs={12} lg={8}>
          <BoxWrapper>
            <TopViewsTable
              data={topviewsData}
              selectedTimeRangeData={selectedTimeRangeData}
            />
          </BoxWrapper>
        </Grid>

        {/* Popular Categories */}
        <Grid item xs={12} lg={4}>
          <BoxWrapper>
            <PopularCategoryChart
              data={popularCategoriesData}
              selectedTimeRangeData={selectedTimeRangeData}
            />
          </BoxWrapper>
        </Grid>

        {/* Popular search keywords */}
        <Grid item xs={12} lg={3}>
          <BoxWrapper>
            <PopularKeywordsList
              data={popularKeywordsData}
              selectedTimeRangeData={selectedTimeRangeData}
            />
          </BoxWrapper>
        </Grid>

        {/* Popular trends */}
        <Grid item xs={12} lg={9}>
          <BoxWrapper>
            <PopularTrends
              data={popularTrendsData.trendingTypes}
              isInTrend={popularTrendsData.sellerInTrend}
            />
          </BoxWrapper>
        </Grid>

        {/* Usage of Product Features */}
        <Grid item xs={12}>
          <BoxWrapper>
            <UsageProductFeatures
              data={usageProductFeaturesData}
              selectedTimeRangeData={selectedTimeRangeData}
            />
          </BoxWrapper>
        </Grid>
      </Grid>
    </>
  );
};

export default DashboardAdmin;
